import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import {
  fieldsToGqlString,
  mergeByIdNewAtTop,
} from '../../../util/shared/vue-global';
import { mergedRecords } from './helper';

const { backend } = clients.direct;

const taxCommonFields = {
  _id: '',
  taxYear: '',
  name1: '',
  name2: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  inCareOf1: '',
  inCareOf2: '',
  emailAddess: '',
  primaryPhone: '',
  taxType: '',
  taxId: '',
  ownerId: '',
  propertyOrParcelId: '',
  schoolDistrict: '',
  originalTaxDistrict: '',
  baseId: '',
  certificate: '',
  homestead: '',
  loanNumber: '',
  invoiceNumber: '',
  situsAddress: '',
  comOwnerAddress: '',
  mtgCode: '',
  deedBkOrPg: '',
  acres: '',
  lots: '',
  land: '',
  originalTax: '',
  improvements: '',
  adjustments: '',
  mobileHome: '',
  totalTax: '',
  assessorPenalty: '',
  totalPaid: '',
  exemptions: '',
  totalDue: '',
  feesAndPen: '',
  statusCode: '',
  legalDesc: '',
  range: '',
  // new fields
  section: '',
  block: '',
  additionDesc: '',
  situsStreetNumber: '',
  situsStreetDirection: '',
  situsStreetName: '',
  situsStreetType: '',
  situsSuffix: '',
  situsTown: '',
  townShip: '',
  lot: '',
  qSec: '',
  mhDescription: '',
  penaltyStartDate: '',
  mhVin: '',
  mhTitle: '',
  mhPropertyOrParcelId: '',
  mhType: '',
  mhMake: '',
  mhName: '',
  mhTag: '',
  mhPrice: '',
  mhLocation: '',
  mhDecal: '',
  mhDecalDate: '',
  mhYearBuilt: '',
  mhTrustReceipt: '',
  totalFee: '',
  addition: '',
  mhTid: '',
  certNumber: '',
  pIdOfRealEstate: '',
  taxIdOfRealEstate: '',
};

const taxFields = {
  ...taxCommonFields,
  commentForTaxInfoChanges: '',
  commentOnlyForOwner: '',
  beforeChange: '',
  afterChange: '',
  afterChangeForTax: '',
  beforeChangeForTax: '',
  commentCreatedDate: '',
};

// Only the two save mutations ask for ownerChanges: it is computed from the diff of that one
// save, so on a plain browse query it is always null and just pads the selection set. The
// caller hands it straight to the category-3 comment it opens (see handleComment), which is
// the only thing that ever reads it.
const taxFieldsOnSave = {
  ...taxFields,
  ownerChanges: { field: '', from: '', to: '' },
};

const taxFieldsForPayments = {
  alertStatusCodes: '',
  allowHalfPayments: '',
  ...taxCommonFields,
};

const omitOrSaUpsertTransaction = {
  ...taxCommonFields,
};

const taxFieldsWithFile = {
  hasFiles: '',
  ...taxFields,
};

const overpaymentsNRefundsFields = {
  _id: '',
  taxYear: '',
  fiscalYear: '',
  taxId: '',
  taxDistrict: '',
  dateOverpaid: '',
  type: '',
  paymentMethod: '',
  taxAmt: '',
  assignedTo: '',
  resolved: '',
  comments: '',
  paidTax: '',
  overPaidAmt: '',
  fund: {
    _id: '',
  },
  paidDate: '',
  issueDate: '',
  warrant: '',
  propertyOrParcelId: '',
};

const calculateTaxesFields = {
  netAssessed: '',
  grossAssessed: '',
  calculatedTax: '',
  totalLevies: '',
  statusCode: '',
};

export const TaxInquiryActions = {
  async loadTaxInquires({ commit, dispatch }, {
    criteria, baseCrudKey, fields, postProcessTrans, signal,
  }) {
    let results;
    try {
      const fieldsOfTax = fields || taxFieldsWithFile;
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query taxTransactions($criteria: taxTransCriteriaType) {
            taxTransactions(criteria: $criteria) {
              ${fieldsToGqlString(fieldsOfTax)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
        context: {
          fetchOptions: {
            signal,
          },
        },
      });
      clients.handleGQLErrors(results, commit);
      const { taxTransactions } = results.data;
      const processedTransData = postProcessTrans
        ? await postProcessTrans(taxTransactions) : taxTransactions;
      if (baseCrudKey) commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      return processedTransData;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax transactions.', { root: true });
    }
    return results.data.taxTransactions;
  },
  async getTaxInquires({ commit, dispatch }, {
    criteria,
  }) {
    let results;
    try {
      const fieldsOfTax = taxFieldsWithFile;
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query taxTransactions($criteria: taxTransCriteriaType) {
            taxTransactions(criteria: $criteria) {
              ${fieldsToGqlString(fieldsOfTax)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        const { taxTransactions } = results.data;
        return taxTransactions;
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax transactions.', { root: true });
    }
    return results.data.taxTransactions;
  },
  async getTaxInquiryForPayment({ commit, dispatch }, {
    criteria, baseCrudKey, fields, signal,
  }) {
    let results;
    try {
      const fieldsOfTax = fields || taxFieldsForPayments;
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query getTaxInquiryForPayment($criteria: taxInquiryForPaymentCriteriaType) {
            getTaxInquiryForPayment(criteria: $criteria) {
              ${fieldsToGqlString(fieldsOfTax)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
        context: {
          fetchOptions: {
            signal,
          },
        },
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        const { getTaxInquiryForPayment } = results.data;
        commit('base/crud/setItems', [baseCrudKey, getTaxInquiryForPayment], { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax transactions.', { root: true });
    }
    return results.data.getTaxInquiryForPayment;
  },
  async upsertTaxInquiryTransaction({ dispatch, rootGetters, commit }, {
    item, baseCrudKey, postProcessTrans, isItOmitOrSa = false,
  }) {
    let results;
    try {
      const fields = isItOmitOrSa ? omitOrSaUpsertTransaction : taxFieldsOnSave;
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation upsertTaxInquiryTransaction ($item: taxTransactionInputType)  {
          upsertTaxInquiryTransaction(upsert: $item) {
            ${fieldsToGqlString(fields)},
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const taxData = rootGetters['base/crud/items'](baseCrudKey);
      const newTaxUnit = results.data.upsertTaxInquiryTransaction;
      const newList = mergeByIdNewAtTop(taxData || [], newTaxUnit);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      return newTaxUnit;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      // Prefer the resolver's own message - it is written for the user (e.g. the
      // duplicate taxYear+taxId rejection) and says what to do about it.
      const gqlMsg = e && e.graphQLErrors && e.graphQLErrors.length && e.graphQLErrors[0].message;
      dispatch('flashError', gqlMsg || 'There was a problem in upserting the tax inquiry', { root: true });
    }
    return null;
  },
  async patchTaxInquiryTransaction({ dispatch, rootGetters, commit }, {
    item, baseCrudKey, postProcessTrans, isItOmitOrSa = false,
  }) {
    let results;
    try {
      const fields = isItOmitOrSa ? omitOrSaUpsertTransaction : taxFieldsOnSave;
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation patchTaxInquiryTransaction ($item: taxTransactionInputType)  {
          patchTaxInquiryTransaction(patch: $item) {
            ${fieldsToGqlString(fields)},
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const taxData = rootGetters['base/crud/items'](baseCrudKey);
      const newTaxUnit = results.data.patchTaxInquiryTransaction;
      const newList = mergeByIdNewAtTop(taxData || [], newTaxUnit);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      return newTaxUnit;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem in patching the tax inquiry', { root: true });
    }
    return null;
  },
  async loadTaxPaymentsWithTaxDetails({ commit, dispatch }, { criteria, baseCrudKey, fields }) {
    let results;
    try {
      const fieldsOfTax = fields || taxFields;
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query taxBrowsePaidBy($criteria: taxPaymentrecordCriteriaType) {
            taxBrowsePaidBy(criteria: $criteria) {
             ${fieldsToGqlString(fieldsOfTax)}
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        const { taxBrowsePaidBy } = results.data;
        commit('base/crud/setItems', [baseCrudKey, taxBrowsePaidBy], { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax payment record.', { root: true });
    }
  },
  async loadPickListResults({ commit, dispatch }, { criteria, fields }) {
    let results;
    const fieldsOfTax = fields || taxFields;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query taxTransactionsPickList($criteria: taxTransCriteriaType) {
            taxTransactionsPickList(criteria: $criteria) {
              ${fieldsToGqlString(fieldsOfTax)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        const { taxTransactionsPickList } = results.data;
        return taxTransactionsPickList;
        // commit('base/crud/setItems', [baseCrudKey, taxPaymentRecord], { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax payment record.', { root: true });
    }
    return {};
  },
  async calculateTaxes({ commit, dispatch }, {
    criteria, fields,
  }) {
    let results;
    try {
      const fieldsOfTax = fields || calculateTaxesFields;
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query calculateTaxes($criteria: calculatedTaxInputType) {
            calculateTaxes(criteria: $criteria) {
              ${fieldsToGqlString(fieldsOfTax)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        const { calculateTaxes } = results.data;

        return calculateTaxes;
      }
      return {};
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax transactions.', { root: true });
    }
    return results.data.taxTransactions;
  },
  async loadOverpaymentsNRefunds({ commit, dispatch }, {
    criteria, baseCrudKey, fields, postProcessTrans,
  }) {
    let results;
    try {
      const fieldsList = fields || overpaymentsNRefundsFields;
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query overpaymentsNRefundsList($criteria: overpaymentsNRefundsCriteriaType) {
            overpaymentsNRefundsList(criteria: $criteria) {
              ${fieldsToGqlString(fieldsList)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        const { overpaymentsNRefundsList } = results.data;
        const processedTransData = postProcessTrans
          ? postProcessTrans(overpaymentsNRefundsList) : overpaymentsNRefundsList;
        commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting Overpayments & Refunds.', { root: true });
    }
    return results.data.taxTransactions;
  },
  async upsertOverpaymentsNRefund({ dispatch, rootGetters, commit }, {
    item, baseCrudKey, postProcessTrans,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation upsertOverpaymentsNRefunds ($item: overpaymentsNRefundsInputType)  {
          upsertOverpaymentsNRefunds(upsert: $item) {
            ${fieldsToGqlString(overpaymentsNRefundsFields)},
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const taxData = rootGetters['base/crud/items'](baseCrudKey);
      const newTaxUnit = results.data.upsertOverpaymentsNRefunds;
      const newList = mergeByIdNewAtTop(taxData || [], newTaxUnit);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      return newTaxUnit;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem in upserting the Overpayments & Refunds.', { root: true });
    }
    return null;
  },
  async updateAssignToOvPayAndrefund({ dispatch, rootGetters, commit },
    { item, baseCrudKey, postProcessTrans }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation updateAssignUser ($item: assignUserInputType)  {
          updateAssignUser(upsert: $item) {
            ${fieldsToGqlString(overpaymentsNRefundsFields)}
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const existingData = rootGetters['base/crud/items'](baseCrudKey);
      const { updateAssignUser } = results.data;
      const newList = mergedRecords((existingData || []), updateAssignUser);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      return updateAssignUser;
    } catch (error) {
      dispatch('flashError', 'There was a problem in upserting the Overpayments & Refunds.', { root: true });
    }
    return {};
  },
  async validatePaidDateOnOverpayment({ dispatch, rootGetters, commit },
    { baseCrudKey, postProcessTrans }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation updateOverpaymentPaidDate {
          updateOverpaymentPaidDate {
            ${fieldsToGqlString(overpaymentsNRefundsFields)}
          }
        }`,
      });
      clients.handleGQLErrors(results, commit);
      const existingData = rootGetters['base/crud/items'](baseCrudKey);
      const { updateOverpaymentPaidDate } = results.data;
      const newList = mergedRecords((existingData || []), updateOverpaymentPaidDate);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      dispatch('flashSuccess', 'Paid date validated successfully on Overpayments & Refunds.', { root: true });
      return updateOverpaymentPaidDate;
    } catch (error) {
      dispatch('flashError', 'There was a problem validating paid date on Overpayments & Refunds.', { root: true });
    }
    return {};
  },
  async checkWarrantNo({ dispatch, commit }, { criteria }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query checkWarNo($criteria: checkWarInputType) {
            checkWarNo(criteria: $criteria) {
              status,
              paidCancelledDate,
              issueDate,
              amount
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      const { checkWarNo } = results.data;
      return checkWarNo;
    } catch (error) {
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'Warrant number not available', { root: true });
    }
    return {};
  },
  async removeMtgCodesFromTaxInquiry({ dispatch, commit },
    { item }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation removeMtgCodesFromTaxInquiry ($item: [taxTransactionInputType])  {
          removeMtgCodesFromTaxInquiry(upserts: $item) {
            statusCodes,
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const { removeMtgCodesFromTaxInquiry } = results.data;
      if (removeMtgCodesFromTaxInquiry.statusCodes === 200) dispatch('flashSuccess', 'Mortgage code removed successfully', { root: true });
      else dispatch('flashError', 'There was a problem while removig mortgage code!', { root: true });
    } catch (error) {
      dispatch('flashError', 'There was a problem while removig mortgage code!', { root: true });
    }
    return {};
  },
  async upsertDecalDateAndDecalNumber({ dispatch, commit },
    { item }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation upsertDecalDateAndDecalNumber ($item: [decalDateAndDecalNumberTypes])  {
          upsertDecalDateAndDecalNumber(upserts: $item) {
            statusCodes,
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const { upsertDecalDateAndDecalNumber } = results.data;
      if (upsertDecalDateAndDecalNumber.statusCodes === 200) dispatch('flashSuccess', 'Decal date and decal number updated successfully', { root: true });
      else dispatch('flashError', 'There was a problem while updating decal date and decal number!', { root: true });
    } catch (error) {
      dispatch('flashError', 'There was a problem while updating decal date and decal number!', { root: true });
    }
    return {};
  },
  async upsertTaxInquiryFixAmounts({ dispatch, rootGetters, commit },
    { item, baseCrudKey, postProcessTrans }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation upsertTaxInquiryFixAmounts ($item: taxTransactionInputType)  {
          upsertTaxInquiryFixAmounts(upsert: $item) {
             ${fieldsToGqlString(taxFields)},
          }
        }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const taxData = rootGetters['base/crud/items'](baseCrudKey);
      const newTaxUnit = results.data.upsertTaxInquiryFixAmounts;
      const newList = mergeByIdNewAtTop(taxData || [], newTaxUnit);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
      return processedTransData;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem in upserting the tax inquiry', { root: true });
    }
    return {};
  },
  async syncOverpaymentWithWarrants({ commit, dispatch }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`{
          syncOverpaymentWithWarrants { statusCode },
        }`,
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data) {
        return results.data.syncOverpaymentWithWarrants;
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem syncing warrants.', { root: true });
    }
    return {};
  },
};

export default TaxInquiryActions;
